import type { DatePickerSlotProps } from '@mui/x-date-pickers/DatePicker'

export const getDatePickerSlotProps = (error?: boolean, { compact = false }: { compact?: boolean } = {}): DatePickerSlotProps<true> => {
  return {
    textField: {
      fullWidth: true,
      variant: 'filled' as const,
      ...(compact ? { hiddenLabel: true, size: 'small' as const } : {}),
      sx: {
        fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        ...(compact && {
          '& .MuiPickersInputBase-root, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersInputBase-section, & .MuiPickersInputBase-sectionContent': {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: 'normal',
          },
          '& .MuiPickersInputBase-sectionsContainer': {
            paddingBlock: 0,
          },
        }),
        // Input button styles
        '& .MuiPickersFilledInput-root': {
          height: compact ? '40px' : '54px',
          ...(compact && { paddingRight: '3px' }),
          backgroundColor: 'white',
          borderRadius: '0.25rem',
          border: error ? `1px solid var(--color-red-400)` : `1px solid var(--color-brand-neutral-400)`,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          // Focus styles
          '&.Mui-focused': {
            borderColor: error ? 'var(--color-red-400)' : 'var(--color-brand-400)',
          },
        },
        '& .MuiIconButton-root:hover': {
          backgroundColor: 'var(--color-brand-neutral-200)',
        },
        // Remove the underline styles
        '& .MuiPickersFilledInput-underline:before': {
          display: 'none',
        },
        '& .MuiPickersFilledInput-underline:after': {
          display: 'none',
        },
      },
    },
    // Year and month picker button styles
    layout: {
      sx: {
        '& .MuiPickersDay-root': {
          fontFamily: 'inherit',
          color: 'var(--color-brand-neutral-900)',
          '&:hover': { backgroundColor: 'var(--color-brand-neutral-200)' },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-brand-500)',
            color: 'white',
            '&:hover, &:focus': { backgroundColor: 'var(--color-brand-600)' },
          },
        },
        // Arrow button to switch between year and month picker
        '& .MuiIconButton-root.MuiIconButton-sizeSmall.MuiPickersCalendarHeader-switchViewButton': {
          border: `1px solid transparent`,
          borderRadius: '50%',
        },
        // Year picker button styles
        '& .MuiYearCalendar-button': {
          fontSize: '14px',
          color: 'var(--color-brand-neutral-900)',
          cursor: 'pointer',
          borderRadius: '20px',
          padding: '2px 4px',
          margin: '1px',
          minWidth: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid var(--color-brand-neutral-300)`,
          '&:hover': {
            backgroundColor: 'var(--color-brand-neutral-200)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-brand-500)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--color-brand-600)',
            },
          },
        },
        // Month picker button styles
        '& .MuiMonthCalendar-button': {
          fontSize: '14px',
          color: 'var(--color-brand-neutral-900)',
          cursor: 'pointer',
          borderRadius: '20px',
          padding: '2px 4px',
          margin: '1px',
          minWidth: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid var(--color-brand-neutral-300)`,
          '&:hover': {
            backgroundColor: 'var(--color-brand-neutral-200)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-brand-500)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--color-brand-600)',
            },
          },
        },
      },
    },
    // Popper styles (from mui-date-picker.css)
    popper: {
      sx: {
        '& .MuiPaper-root': {
          zIndex: 1500,
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          paddingBottom: '10px',
          maxHeight: '360px',
        },
      },
    },
    // Calendar header styles
    calendarHeader: {
      sx: {
        '& .MuiPickersCalendarHeader-label': {
          fontSize: '14px',
          color: 'var(--color-brand-neutral-900)',
        },
        '& .MuiPickersArrowSwitcher-button': {
          color: 'var(--color-brand-neutral-900)',
          '&:hover': {
            backgroundColor: 'var(--color-brand-neutral-200)',
          },
        },
      },
    },
  }
}
